"use client"

import { AppChannel } from "@/config/channel"
import { useConfigParams } from "@/hooks/use-config-params"
import { postLoginRedirect } from "@/lib/postLoginRedirect"
import { useLoginDataWithMfaConfirm } from "@/services/hooks/use-login-data"
import { useJumioMutation } from "@/services/post-jumio"
import { isOk } from "@pacote/result"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import { ModalInfoDocumentUploaded } from "./modal-info-doc-upload"

const Jumio = () => {
  /*--------------------hooks--------------------*/
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)
  const [showModalInfoDocument, setShowModalInfoDocument] = useState(false)
  const router = useRouter()
  const { channel } = useConfigParams()

  const searchParams = useSearchParams()
  const { trigger } = useJumioMutation()
  const loginData = useLoginDataWithMfaConfirm()

  /*--------------------config--------------------*/
  const icQueryParam = searchParams.get("ic")
  const selfieParam = searchParams.get("selfie")
  /*--------------------helpers--------------------*/

  const onCloseModal = () => {
    setShowModalInfoDocument(false)

    if (channel === AppChannel.AppSisalPoker)
      return router.push(`/?${searchParams.toString()}`)

    postLoginRedirect(`${window.APP_MAIN_SITE_HOMEPAGE}`)
  }

  /*--------------------effects--------------------*/
  useEffect(() => {
    const iframeEventListener = (event: unknown) => {
      if (
        event instanceof Object &&
        "data" in event &&
        typeof event.data === "string"
      ) {
        try {
          const eventData = JSON.parse(event.data)
          if (eventData.payload && eventData.payload.value === "success") {
            setShowModalInfoDocument(true)
          }
        } catch (error) {
          console.error("ERROR: ", error)
        }
      }
    }

    const getIframeUrl = async (billCodeId: number) => {
      const selfie = selfieParam === "true" || selfieParam === "Y"
      const response = await trigger({ billCodeId, condition: selfie })

      if (isOk(response)) {
        setIframeUrl(response.value.url)
      }
      //non facciamo nulla quì perchè in caso di errore facciamo il throw dentro il service
      //e verrà bloccato da swr con redirect a pagina di errore
    }

    const getBillCodeForIframeUrlCall = () => {
      const validBillCodeFromQueryParam = icQueryParam !== null
      const validBillCodeFromState =
        loginData?.billCodeId != null && loginData.billCodeId !== 0
      const validBillCode =
        validBillCodeFromQueryParam || validBillCodeFromState

      if (!validBillCode) return null

      const billCodeFromQueryParam =
        icQueryParam != null ? parseInt(icQueryParam, 10) : NaN
      const billCode = !Number.isNaN(billCodeFromQueryParam)
        ? billCodeFromQueryParam
        : loginData?.billCodeId

      return billCode
    }

    const billCode = getBillCodeForIframeUrlCall()

    if (!billCode) {
      throw new Error("Jumio - invalid bill code")
    }

    getIframeUrl(billCode)

    window.addEventListener("message", iframeEventListener, false)

    return () => {
      window.removeEventListener("message", iframeEventListener)
    }
  }, [icQueryParam, loginData?.billCodeId, selfieParam, trigger])

  return (
    <>
      {iframeUrl && (
        <div className="flex h-[calc(100vh-3.125rem)] items-center justify-center overflow-hidden">
          <iframe
            title="jumio"
            src={iframeUrl}
            className="shadow-[2px_2px_16px_rgba(0, 0, 0.15)] size-full overflow-hidden border-none"
            allow="camera;fullscreen;accelerometer;gyroscope;magnetometer"
            allowFullScreen
          />
        </div>
      )}
      {showModalInfoDocument && (
        <ModalInfoDocumentUploaded onClose={onCloseModal} />
      )}
    </>
  )
}

export { Jumio }
